import React, { useContext, useState, ReactNode } from "react";
import { AdminUnit } from "types";

export interface User {
  id: string;
  access: string;
  refresh?: string;
  user_id: string;
  username: string;
  email?: string;
  permissions: string[];
  roles: string[];
  employee_id: string;
  admin_unit: string;
  org_unit: string;
  user_type: string;
  is_superuser?: boolean;
  full_name: string;
}

interface AuthState {
  user: User;
  adminUnitDetail: AdminUnit | {};
  token: string;
}

type UpdateAuth = (auth: AuthState) => void;

// Provide default values (optional safe fallback values)
const defaultUser: User = {
  id: "",
  access: "",
  refresh: "",
  user_id: "",
  username: "",
  email: "",
  permissions: [],
  roles: [],
  employee_id: "",
  admin_unit: "",
  org_unit: "",
  user_type: "",
  is_superuser: false,
  full_name: "",
};

const defaultAdminUnit: AdminUnit = {
  hierarchy_level: "",
  parent_unit: null,
  structure_level: "",
  unit_name: "",
  uu_id: "",
};

// Create contexts
const AuthContext = React.createContext<AuthState>({
  user: defaultUser,
  adminUnitDetail: defaultAdminUnit,
  token: "",
});

const AuthUpdateContext = React.createContext<UpdateAuth>(() => {});

// Custom hooks
export function useAuth() {
  return useContext(AuthContext);
}

export function useAuthUpdate() {
  return useContext(AuthUpdateContext);
}

export function useHasRole(requiredRoles: string[]): boolean {
  const { user } = useAuth();
  
  if (!user || !user.roles || user.roles.length === 0) {
    return false;
  }

  if (user.is_superuser) {
    return true;
  }

  return user.roles.some((role) => requiredRoles.includes(role));
}

// Provider
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: defaultUser,
    adminUnitDetail: defaultAdminUnit,
    token: "",
  });

  const updateAuth: UpdateAuth = (auth) => {
    setState(auth);
  };

  return (
    <AuthContext.Provider value={state}>
      <AuthUpdateContext.Provider value={updateAuth}>
        {children}
      </AuthUpdateContext.Provider>
    </AuthContext.Provider>
  );
}
